import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { QUESTIONS, DEFAULT_ANSWERS } from "@/lib/questions";
import { recommend } from "@/lib/api";
import QuestionStep from "./QuestionStep";
import LiveScorePanel from "./LiveScorePanel";
import RecommendationResult from "@/components/result/RecommendationResult";
import WaveDivider from "@/components/layout/WaveDivider";
import { ChevronLeft, ChevronRight, RotateCcw, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { newProjectId } from "@/lib/storage";

export default function Wizard({ features, loading, initialProject, onProjectConsumed, onContact, onOpenInConfigurator }) {
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS());
  const [step, setStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState(newProjectId());
  const [pending, setPending] = useState(false);
  const [loadedConfig, setLoadedConfig] = useState(null);
  const [loadedCode, setLoadedCode] = useState(null);

  // Load initial project if provided
  useEffect(() => {
    if (initialProject) {
      setAnswers({ ...DEFAULT_ANSWERS(), ...initialProject.answers });
      setProjectName(initialProject.name || "");
      setProjectId(initialProject.id);
      setLoadedConfig(initialProject.configuration || null);
      setLoadedCode(initialProject.code || null);
      setShowResult(true);
      onProjectConsumed && onProjectConsumed();
    }
  }, [initialProject, onProjectConsumed]);

  const current = QUESTIONS[step];
  const total = QUESTIONS.length;
  const progress = Math.round(((step + (showResult ? 1 : 0)) / total) * 100);

  // Live recommendation - debounced-ish (fire on every answer change)
  const computeRecommendation = useCallback(async (ans) => {
    try {
      setPending(true);
      const r = await recommend(ans);
      setRecommendation(r);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    computeRecommendation(answers);
  }, [answers, computeRecommendation]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const canProceed = useMemo(() => {
    if (!current) return false;
    // For questions with an exact-value field, accept either the bucket or the exact number
    if (current.exactField) {
      const exact = answers[current.exactField];
      if (exact && Number(exact) > 0) return true;
    }
    const v = answers[current.key];
    if (current.type === "single") return !!v;
    if (current.type === "boolean") return v === true || v === false;
    if (current.type === "multi") return true; // multi is optional
    return false;
  }, [current, answers]);

  const handleNext = () => {
    if (step < total - 1) {
      setStep((s) => s + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (showResult) {
      setShowResult(false);
      return;
    }
    if (step > 0) setStep((s) => s - 1);
  };

  const handleReset = () => {
    setAnswers(DEFAULT_ANSWERS());
    setStep(0);
    setShowResult(false);
    setProjectName("");
    setProjectId(newProjectId());
    toast.info("Assistent zurückgesetzt.");
  };

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground">Lädt Produktdaten …</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4">
        {/* Progress + title */}
        <Card className="p-4 md:p-5 border-border bg-card/80 card-elev">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Schritt {showResult ? total : step + 1} / {total}</div>
                <div className="font-semibold">{showResult ? "Ergebnis" : current?.label?.slice(0, 60)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                data-testid="project-name-input"
                placeholder="Projektname (optional)"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-56 h-9 bg-secondary/40 border-border"
              />
              <Button data-testid="btn-reset" variant="ghost" size="sm" onClick={handleReset}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Zurücksetzen
              </Button>
            </div>
          </div>
          <Progress data-testid="wizard-progress" value={progress} className="h-2" />
        </Card>

        {/* Content */}
        <Card className="p-6 md:p-8 border-border bg-card card-elev min-h-[420px]">
          <AnimatePresence mode="wait">
            {!showResult ? (
              <motion.div
                key={`step-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <QuestionStep
                  question={current}
                  value={answers[current.key]}
                  onChange={(v) => setAnswer(current.key, v)}
                  extra={current.exactField ? answers[current.exactField] : undefined}
                  onExtraChange={
                    current.exactField
                      ? (v) => setAnswer(current.exactField, v)
                      : undefined
                  }
                />
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <RecommendationResult
                  recommendation={recommendation}
                  answers={answers}
                  questions={QUESTIONS}
                  projectName={projectName}
                  setProjectName={setProjectName}
                  projectId={projectId}
                  features={features}
                  onContact={onContact}
                  initialConfig={loadedConfig}
                  initialCode={loadedCode}
                  onOpenInConfigurator={onOpenInConfigurator}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {!showResult && (
            <>
              <div className="mt-8">
                <WaveDivider height={28} opacity={0.7} />
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <Button
                  data-testid="btn-back"
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Zurück
                </Button>
                <div className="text-xs text-muted-foreground">
                  {pending ? "Empfehlung wird berechnet …" : " "}
                </div>
                <Button
                  data-testid="btn-next"
                  onClick={handleNext}
                  disabled={!canProceed}
                  className="bg-primary text-primary-foreground hover:opacity-90"
                >
                  {step === total - 1 ? "Zum Ergebnis" : "Weiter"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </>
          )}

          {showResult && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
              <Button data-testid="btn-back-from-result" variant="outline" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Antworten ändern
              </Button>
              <Button data-testid="btn-restart" variant="ghost" onClick={handleReset}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Neues Projekt
              </Button>
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-4">
        <LiveScorePanel recommendation={recommendation} />
      </div>
    </div>
  );
}
